import React from 'react'
import { UICheckbox } from './ui-checkbox'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { ChevronRightIcon, ChevronDownIcon, FolderIcon, FileIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { UIBox } from '../layout/ui-box'

export interface UICheckboxTreeNode {
  id: string
  label: string
  description?: string
  children?: UICheckboxTreeNode[]
  disabled?: boolean
  icon?: LucideIcon
  metadata?: Record<string, any>
}

export interface UICheckboxTreeProps {
  /**
   * 트리 데이터
   */
  data: UICheckboxTreeNode[]
  /**
   * 선택된 노드 ID들
   */
  value?: string[]
  /**
   * 기본 선택된 노드 ID들
   */
  defaultValue?: string[]
  /**
   * 값 변경 핸들러
   */
  onValueChange?: (value: string[]) => void
  /**
   * 노드 선택 핸들러 (개별 노드)
   */
  onNodeChange?: (nodeId: string, checked: boolean | 'indeterminate', node: UICheckboxTreeNode) => void
  /**
   * 체크박스 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 접힘/펼침 가능 여부
   */
  collapsible?: boolean
  /**
   * 기본 펼쳐진 노드들
   */
  defaultExpanded?: string[]
  /**
   * 스타일 변형
   */
  variant?: 'default' | 'compact'
  /**
   * 기본 아이콘 사용 여부
   */
  showDefaultIcons?: boolean
  /**
   * 컨테이너 클래스명
   */
  className?: string
}

const sizeVariants = {
  sm: {
    checkbox: "sm",
    icon: "size-3",
    chevron: "size-3",
    spacing: "gap-1.5",
    padding: "py-1"
  },
  default: {
    checkbox: "default",
    icon: "size-4",
    chevron: "size-4",
    spacing: "gap-2.5",
    padding: "py-1.5"
  },
  lg: {
    checkbox: "lg",
    icon: "size-5",
    chevron: "size-5",
    spacing: "gap-3",
    padding: "py-2"
  }
}

const variantStyles = {
  default: {
    node: "py-1.5 px-2 rounded hover:bg-accent/5 transition-colors",
    content: "space-y-1"
  },
  compact: {
    node: "py-1 px-1 rounded-sm hover:bg-accent/5 transition-colors",
    content: "space-y-0.5"
  }
}

/**
 * 간소화된 계층적 구조의 체크박스 트리 컴포넌트
 * 부모-자식 관계에서 indeterminate 상태를 자동으로 관리합니다.
 */
export const UICheckboxTree = React.forwardRef<
  HTMLDivElement,
  UICheckboxTreeProps
>((
  {
    data,
    value,
    defaultValue = [],
    onValueChange,
    onNodeChange,
    size = "default",
    collapsible = true,
    defaultExpanded = [],
    variant = "default",
    showDefaultIcons = true,
    className,
    ...boxProps
  },
  ref
) => {
  const [internalValue, setInternalValue] = React.useState<string[]>(value || defaultValue)
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set(defaultExpanded))

  const variants = sizeVariants[size]
  const styles = variantStyles[variant]

  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value)
    }
  }, [value])


  // 모든 자식 노드 ID를 재귀적으로 수집
  const getAllChildIds = (node: UICheckboxTreeNode): string[] => {
    const childIds = [node.id]
    if (node.children) {
      node.children.forEach(child => {
        childIds.push(...getAllChildIds(child))
      })
    }
    return childIds
  }

  // 노드의 체크 상태 확인
  const getNodeCheckState = (node: UICheckboxTreeNode): boolean | 'indeterminate' => {
    if (!node.children || node.children.length === 0) {
      return internalValue.includes(node.id)
    }

    const childStates = node.children.map(child => getNodeCheckState(child))
    const checkedCount = childStates.filter(state => state === true).length
    const indeterminateCount = childStates.filter(state => state === 'indeterminate').length

    if (checkedCount === node.children.length) {
      return true
    } else if (checkedCount > 0 || indeterminateCount > 0) {
      return 'indeterminate'
    } else {
      return false
    }
  }

  // 노드 체크 상태 변경 처리
  const handleNodeChange = (node: UICheckboxTreeNode, checked: boolean | 'indeterminate') => {
    const allChildIds = getAllChildIds(node)
    let newValue: string[]

    if (checked === true) {
      // 모든 자식 노드 선택
      newValue = [...new Set([...internalValue, ...allChildIds])]
    } else {
      // 모든 자식 노드 선택 해제
      newValue = internalValue.filter(id => !allChildIds.includes(id))
    }

    setInternalValue(newValue)
    onValueChange?.(newValue)
    onNodeChange?.(node.id, checked, node)
  }

  // 노드 펼침/접힘 토글
  const toggleExpanded = (nodeId: string) => {
    const newExpanded = new Set(expanded)
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId)
    } else {
      newExpanded.add(nodeId)
    }
    setExpanded(newExpanded)
  }

  // 노드 렌더링
  const renderNode = (node: UICheckboxTreeNode, level: number = 0) => {
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expanded.has(node.id)
    const checkState = getNodeCheckState(node)
    const nodeId = `tree-node-${node.id}`

    // 기본 아이콘 선택
    const DefaultIcon = hasChildren ? FolderIcon : FileIcon
    const Icon = node.icon || (showDefaultIcons ? DefaultIcon : undefined)

    return (
      <UIBox key={node.id} className={styles.content}>
        {/* Node container */}
        <UIBox
          className={cn(
            "group flex items-center",
            styles.node,
            variants.spacing,
            variants.padding
          )}
          style={{ paddingLeft: `${level * 1.5}rem` }}
        >

          {/* Expand/collapse button */}
          {hasChildren && collapsible ? (
            <button
              type="button"
              onClick={() => toggleExpanded(node.id)}
              className={cn(
                "shrink-0 flex items-center justify-center rounded-sm",
                "hover:bg-accent/20 transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-accent/50",
                variants.chevron === 'size-3' ? "size-6" :
                variants.chevron === 'size-4' ? "size-7" : "size-8"
              )}
              aria-label={isExpanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
            >
              {isExpanded ? (
                <ChevronDownIcon className={cn(variants.chevron, "text-muted-foreground")} />
              ) : (
                <ChevronRightIcon className={cn(variants.chevron, "text-muted-foreground")} />
              )}
            </button>
          ) : (
            <UIBox className={cn(
              "shrink-0",
              variants.chevron === 'size-3' ? "size-6" :
              variants.chevron === 'size-4' ? "size-7" : "size-8"
            )} />
          )}

          {/* Checkbox */}
          <UICheckbox
            id={nodeId}
            checked={checkState}
            onCheckedChange={(newChecked) => handleNodeChange(node, newChecked)}
            size={variants.checkbox as 'sm' | 'default' | 'lg'}
            disabled={node.disabled}
            variant="indeterminate"
            className="shrink-0"
          />

          {/* Icon */}
          {Icon && (
            <Icon className={cn(
              variants.icon,
              "shrink-0 text-muted-foreground transition-colors",
              "group-hover:text-accent",
              (checkState === true || checkState === 'indeterminate') && "text-accent"
            )} />
          )}

          {/* Label and description */}
          <UIBox className="flex-1 min-w-0">
            <UILabel
              htmlFor={nodeId}
              className={cn(
                "block font-medium cursor-pointer transition-colors",
                size === 'sm' && "text-sm",
                size === 'lg' && "text-lg",
                "text-foreground group-hover:text-accent",
                (checkState === true || checkState === 'indeterminate') && "text-accent",
                node.disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              {node.label}
            </UILabel>
            {node.description && (
              <UITypography
                variant="p"
                color="muted"
                className={cn(
                  "mt-0.5",
                  size === 'sm' && "text-xs",
                  size === 'default' && "text-sm",
                  size === 'lg' && "text-base"
                )}
              >
                {node.description}
              </UITypography>
            )}
          </UIBox>
        </UIBox>

        {/* Children */}
        {hasChildren && (!collapsible || isExpanded) && (
          <UIBox className="relative ml-6">
            <UIBox className={styles.content}>
              {node.children!.map(child => renderNode(child, level + 1))}
            </UIBox>
          </UIBox>
        )}
      </UIBox>
    )
  }

  return (
    <UIBox ref={ref} className={cn("space-y-1", className)} {...boxProps}>
      {data.map(node => renderNode(node))}
    </UIBox>
  )
})

UICheckboxTree.displayName = "UICheckboxTree"